/**
 * `Toggle` — ported from `Foyer_Dashboard_CURRENT.jsx` line 316.
 * Animated on/off switch keyed to the lime accent.
 */

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { useC } from '../../lib/theme.js';

export interface ToggleProps {
  on: boolean;
  onChange?: (next: boolean) => void;
  /** When true the switch is inert (no toggle) and rendered dimmed —
   *  e.g. a payment-gated skill before a rail is connected. */
  disabled?: boolean;
  'aria-label'?: string;
}

export function Toggle({ on, onChange, disabled = false, ...aria }: ToggleProps): JSX.Element {
  const C = useC();
  const [state, setState] = useState(on);
  useEffect(() => setState(on), [on]);
  const handle = (): void => {
    if (disabled) return;
    const next = !state;
    setState(next);
    onChange?.(next);
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      aria-pressed={state}
      aria-disabled={disabled}
      aria-label={aria['aria-label']}
      className="relative transition-all flex-shrink-0"
      style={{
        width: 36,
        height: 20,
        background: state ? C.lime : C.surface2,
        border: `1px solid ${state ? C.lime : C.border}`,
        borderRadius: 12,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <motion.div
        animate={{ x: state ? 16 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        style={{
          position: 'absolute',
          top: 2,
          width: 14,
          height: 14,
          background: state ? C.ink : C.muted,
          borderRadius: '50%',
        }}
      />
    </button>
  );
}
